import React, { useState } from 'react';
import { usePlayerPrograms } from '@/hooks/usePlayerPrograms';
import { useTeamPlayers } from '@/hooks/useTeamPlayers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Trash2, Clock } from 'lucide-react';
import { VerifiedAvatar } from '@/components/ui/verified-avatar';
import { toast } from 'sonner';

interface ProgramPlayerAssignmentsProps {
  programId: string;
  teamId: string;
  programTitle: string;
  onBack: () => void;
}

export const ProgramPlayerAssignments = ({ 
  programId, 
  teamId, 
  programTitle,
  onBack 
}: ProgramPlayerAssignmentsProps) => {
  const { playerPrograms, loading, assignProgram, removeAssignment, updateAssignmentStatus } = usePlayerPrograms(undefined, programId);
  const { players, loading: playersLoading } = useTeamPlayers(teamId);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Filter available players (not already assigned to this program)
  const availablePlayers = players.filter(player => 
    !playerPrograms.some(pp => pp.player_id === player.id)
  );

  const handleAssignPlayer = async () => {
    if (!selectedPlayerId) {
      toast.error('Please select a player');
      return;
    }

    setIsAssigning(true);
    try {
      const success = await assignProgram(selectedPlayerId, programId);
      if (success) {
        setSelectedPlayerId('');
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAssignment = async (playerProgramId: string) => {
    await removeAssignment(playerProgramId);
  };

  const handleStatusChange = async (playerProgramId: string, newStatus: string) => {
    await updateAssignmentStatus(playerProgramId, newStatus);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading || playersLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading assignments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Assign New Player */}
      {availablePlayers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Assign Player to {programTitle}
            </CardTitle>
            <CardDescription>
              Assign team players to this training program
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium">Select Player</label>
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a player..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePlayers.map((player) => (
                      <SelectItem key={player.id} value={player.id}>
                        {player.profile.first_name} {player.profile.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleAssignPlayer} 
                disabled={!selectedPlayerId || isAssigning}
              >
                {isAssigning ? 'Assigning...' : 'Assign Player'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assigned Players ({playerPrograms.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {playerPrograms.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Players Assigned</h3>
              <p className="text-muted-foreground">
                No players are currently assigned to this program.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {playerPrograms.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <VerifiedAvatar
                      src=""
                      alt={`${assignment.player?.profile?.first_name} ${assignment.player?.profile?.last_name}`}
                      fallback={`${assignment.player?.profile?.first_name?.[0]}${assignment.player?.profile?.last_name?.[0]}`}
                      isVerified={false}
                    />
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-medium">
                          {assignment.player?.profile?.first_name} {assignment.player?.profile?.last_name}
                        </h4>
                        <Badge className={getStatusColor(assignment.status)}>
                          {assignment.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={assignment.status}
                      onValueChange={(value) => handleStatusChange(assignment.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};